import { SbMenu, SbMenuItem } from '@Components/Menu';
import SbTooltip from '@Components/Tooltip';
import {
  downloadCarrierLabel,
  downloadProformaInvoice,
  downloadShippingInvoice,
  downloadShippingLabel,
} from '@Reducers/ShipmentHistory/actions';
import { Box, Grid } from '@mui/material';
import React, { useState } from 'react';
import { actionIconStyle } from '../../pages/ViewSingleShipment/style';
import { AppDispatch, RootStateType } from '@Store';
import { useDispatch, useSelector } from 'react-redux';
import Image from '@Components/Image';
import { ACTION_NAMES, PERFORM_ACTION } from '@Constants/actionsNames';
import { isActionAllowed } from '../../utils/allowedActions';
import { userLogin } from '@Services/hasAdminAccess';

// Icon imports - semantically appropriate for each action
import VisibilityIcon from '@mui/icons-material/Visibility';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DescriptionIcon from '@mui/icons-material/Description';
import InventoryIcon from '@mui/icons-material/Inventory';
import ReceiptIcon from '@mui/icons-material/Receipt';
import HistoryIcon from '@mui/icons-material/History';
import ShipmentHistoryModal from './ShipmentHistoryModal';

interface PropsType {
  handleView?: (e: React.MouseEvent<SVGSVGElement>) => void;
  shipmentNumber: string;
  hideCarrierAWB?: boolean;
  carrierAWBIcon?: string;
  shipmentId?: string;
}

function DownloadShipmentLabelAndInvoice({
  handleView,
  shipmentNumber,
  hideCarrierAWB,
  carrierAWBIcon,
  shipmentId,
}: PropsType) {
  const dispatch = useDispatch<AppDispatch>();
  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationPublicData);
  const { name = '' } = organizationData || {};
  const { menuAcccessGroupById: accessGroupById } = useSelector((state: RootStateType) => state.accessGroup);
  const isUserLogin = userLogin();
  const [open, setOpen] = useState<boolean>(false);
  const shipmentById = useSelector((state: RootStateType) => state.adminShipment?.shipmentById);

  const isViewDocsAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Shipment_Details.view_docs,
    PERFORM_ACTION.write,
  );
  const isDownloadShipmentlabelAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Shipment_Details.download_shipment_label,
    PERFORM_ACTION.write,
  );
  const isDownloadProformaInvoiceAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Shipment_Details.download_proforma_invoice,
    PERFORM_ACTION.write,
  );

  const id = shipmentId || shipmentById?._id;

  return (
    <Grid container item justifyContent="space-around" style={{ width: '175px' }}>
      {handleView && isViewDocsAllowed && (
        <SbTooltip title="View Document">
          <VisibilityIcon style={actionIconStyle} onClick={handleView} />
        </SbTooltip>
      )}

      {isDownloadShipmentlabelAllowed && name !== 'Cargo Solutions Network' && (
        <SbMenu icon={<LocalShippingIcon />} title="Shipping Label">
          <SbMenuItem
            name="A4"
            onClick={() => {
              dispatch(downloadShippingLabel({ shipmentNumber, formatType: 'A4' }));
            }}
          ></SbMenuItem>
          <SbMenuItem
            name="A5"
            onClick={() => {
              dispatch(downloadShippingLabel({ shipmentNumber, formatType: 'A5' }));
            }}
          ></SbMenuItem>
        </SbMenu>
      )}

      {!hideCarrierAWB && (
        <SbTooltip title="Carrier AWB">
          <Box
            className="font-medium md-text text-decoration-none sb-text-black relative cursor-pointer"
            onClick={() => {
              if (carrierAWBIcon) {
                dispatch(downloadCarrierLabel(shipmentNumber));
              }
            }}
          >
            {carrierAWBIcon ? (
              <Image
                src={carrierAWBIcon || ''}
                alt="logo"
                style={{
                  height: '22px',
                  width: '44px',
                  position: 'relative',
                  zIndex: 1,
                }}
              />
            ) : (
              <InventoryIcon
                sx={{
                  position: 'relative',
                  zIndex: 1,
                }}
              />
            )}
          </Box>
        </SbTooltip>
      )}

      <SbMenu icon={<ReceiptIcon />} title="Shipment Invoice">
        <SbMenuItem
          name="PDF"
          onClick={() => {
            dispatch(downloadShippingInvoice({ shipmentNumber, fileType: 'pdf' }));
          }}
        ></SbMenuItem>
        <SbMenuItem
          name="XLSX"
          onClick={() => {
            dispatch(downloadShippingInvoice({ shipmentNumber, fileType: 'xlsx' }));
          }}
        ></SbMenuItem>
      </SbMenu>

      {isDownloadProformaInvoiceAllowed && (
        <SbTooltip title="Proforma Invoice">
          <DescriptionIcon
            style={actionIconStyle}
            onClick={() => dispatch(downloadProformaInvoice({ shipmentNumber }))}
          />
        </SbTooltip>
      )}

      {!isUserLogin && (
        <SbTooltip title="Shipment History Log">
          <HistoryIcon style={actionIconStyle} onClick={() => setOpen(true)} />
        </SbTooltip>
      )}

      {id && !isUserLogin && <ShipmentHistoryModal open={open} onClose={() => setOpen(false)} shipmentId={id} />}
    </Grid>
  );
}

export default DownloadShipmentLabelAndInvoice;
